import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";

const STORAGE_KEY = "draggableBackButtonPosition";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const DraggableBackButton = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const menu = useSelector((state) => state.menu?.data || []);
	const [position, setPosition] = useState(() => {
		if (typeof window === "undefined") {
			return { left: 24, top: 24 };
		}
		const saved = window.localStorage.getItem(STORAGE_KEY);
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				if (
					parsed &&
					typeof parsed.left === "number" &&
					typeof parsed.top === "number"
				) {
					return parsed;
				}
			} catch (error) {
				// ignore parse errors
			}
		}
		return {
			left: Math.max(24, window.innerWidth - 100),
			top: Math.max(24, window.innerHeight - 100),
		};
	});
	const [showOptions, setShowOptions] = useState(false);

	const dragState = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });

	useEffect(() => {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
	}, [position]);

	const resolveDynamicRoute = useCallback((to) => {
		if (!to || typeof to !== "string") return to;
		try {
			const userContextRaw = window.localStorage.getItem("userContext");
			if (!userContextRaw) return to;
			const ctx = JSON.parse(userContextRaw);
			let resolved = to;
			resolved = resolved.replace(/{companyId}/g, ctx.companyId || "");
			resolved = resolved.replace(/{branchId}/g, ctx.branchId || "");
			resolved = resolved.replace(/{assignmentId}/g, ctx.assignmentId || "");
			return resolved;
		} catch (err) {
			return to;
		}
	}, []);

	const normalizePath = useCallback((path) => {
		if (!path) return "";
		return path.replace(/\/+$/, "");
	}, []);

	const currentPath = normalizePath(location.pathname);

	const findActiveMenu = useCallback(
		(items) => {
			if (!items || !Array.isArray(items)) return null;
			for (const item of items) {
				const itemPath = normalizePath(resolveDynamicRoute(item.to));
				if (itemPath && (currentPath === itemPath || currentPath.startsWith(itemPath + "/"))) {
					return item;
				}
				if (item.content && Array.isArray(item.content)) {
					const childMatch = findActiveMenu(item.content);
					if (childMatch) {
						return item;
					}
				}
			}
			return null;
		},
		[currentPath, normalizePath, resolveDynamicRoute],
	);

	const activeModule = findActiveMenu(menu);
	const hasModule = Boolean(activeModule && activeModule.title);

	const handlePointerMove = useCallback((event) => {
		if (!dragState.current.isDragging) return;
		const nextLeft = dragState.current.startLeft + (event.clientX - dragState.current.startX);
		const nextTop = dragState.current.startTop + (event.clientY - dragState.current.startY);
		setPosition({
			left: clamp(nextLeft, 0, window.innerWidth - 90),
			top: clamp(nextTop, 0, window.innerHeight - 70),
		});
	}, []);

	const handlePointerUp = useCallback(() => {
		dragState.current.isDragging = false;
		window.removeEventListener("pointermove", handlePointerMove);
		window.removeEventListener("pointerup", handlePointerUp);
	}, [handlePointerMove]);

	const handlePointerDown = useCallback((event) => {
		if (event.button !== 0) return;
		dragState.current = {
			isDragging: true,
			startX: event.clientX,
			startY: event.clientY,
			startLeft: position.left,
			startTop: position.top,
		};
		window.addEventListener("pointermove", handlePointerMove);
		window.addEventListener("pointerup", handlePointerUp);
	}, [handlePointerMove, handlePointerUp, position.left, position.top]);

	const handleToggleOptions = (event) => {
		event.stopPropagation();
		setShowOptions((prev) => !prev);
	};

	const moduleLabel = hasModule ? activeModule.title : null;
	const moduleRoute = hasModule
		? resolveDynamicRoute(activeModule.to || activeModule.content?.[0]?.to || "/dashboard")
		: "/dashboard";

	return (
		<div
			style={{
				position: "fixed",
				left: position.left,
				top: position.top,
				zIndex: 1100,
				cursor: "grab",
				userSelect: "none",
				touchAction: "none",
				overflow: "visible",
			}}
			onPointerDown={handlePointerDown}
		>
			<Button
				variant="primary"
				size="sm"
				onClick={handleToggleOptions}
				style={{
					borderRadius: "999px",
					padding: "0.55rem 0.9rem",
					boxShadow: "none",
					color: "#ffffff",
				}}
			>
				<i className={`fas fa-${showOptions ? "minus" : "plus"}`}></i>
			</Button>

			{showOptions && (
				<div
					style={{
						position: "absolute",
						top: 0,
						left: position.left < (typeof window !== "undefined" ? window.innerWidth / 2 : 0) ? "100%" : undefined,
						right: position.left >= (typeof window !== "undefined" ? window.innerWidth / 2 : 0) ? "100%" : undefined,
						marginLeft: position.left < (typeof window !== "undefined" ? window.innerWidth / 2 : 0) ? 8 : 0,
						marginRight: position.left >= (typeof window !== "undefined" ? window.innerWidth / 2 : 0) ? 8 : 0,
						borderRadius: 12,
						padding: 8,
						boxShadow: "none",
						display: "flex",
						alignItems: "center",
						gap: 8,
						flexWrap: "nowrap",
					}}
				>
					<Button
						variant="primary"
						size="sm"
						className="d-flex align-items-center"
						style={{
							color: "#ffffff",
							whiteSpace: "nowrap",
						}}
						onClick={() => {
						setShowOptions(false);
						navigate(-1);
					}}
					>
						<i className="fas fa-arrow-left me-2"></i>
						Back
					</Button>
					{hasModule && (
						<Button
							variant="primary"
							size="sm"
							className="d-flex align-items-center"
							style={{
								color: "#ffffff",
								whiteSpace: "nowrap",
							}}
							onClick={() => {
							setShowOptions(false);
							navigate(moduleRoute);
							}}
						>
							<i className="fas fa-layer-group me-2"></i>
							Back to {moduleLabel}
						</Button>
					)}
					<Button
						variant="primary"
						size="sm"
						className="d-flex align-items-center"
						style={{
							color: "#ffffff",
							whiteSpace: "nowrap",
						}}
						onClick={() => {
						setShowOptions(false);
						navigate("/dashboard");
					}}
					>
						<i className="fas fa-home me-2"></i>
						Home
					</Button>
				</div>
			)}
		</div>
	);
};

export default DraggableBackButton;
